import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Mail,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  X,
  Search,
} from 'lucide-react';
import apiClient from '@/services/api';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { API_ENDPOINTS } from '@/config/api.config';

// ─── Types ───────────────────────────────────────────────────────────
export interface EmailComposeData {
  email_subject: string;
  email_html_body: string;
  cc_emails: string[];
  bcc_emails: string[];
}

export interface EmailRecipient {
  id: string;
  name: string;
  email: string;
  type: 'user' | 'group' | 'role';
}

export type EmailTargetType = 'ALL' | 'USER' | 'GROUP' | 'ROLE';

interface EmailComposePanelProps {
  value: EmailComposeData;
  onChange: (data: EmailComposeData) => void;
  selectedRecipients: EmailRecipient[];
  onAddRecipient: (user: EmailRecipient) => void;
  onRemoveRecipient: (userId: string) => void;
  targetType: EmailTargetType;
}

// ─── Avatar color generation (deterministic by name) ─────────────────
const AVATAR_COLORS = [
  '#0078D4', '#107C10', '#B4009E', '#D83B01',
  '#008272', '#5C2D91', '#C239B3', '#00B7C3',
  '#E3008C', '#4A154B', '#498205', '#CA5010',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

// ─── Outlook-style To field — constrained by targetType ───────────────
function RecipientSearchInput({
  selectedRecipients,
  onAdd,
  onRemove,
  targetType,
}: {
  selectedRecipients: EmailRecipient[];
  onAdd: (user: EmailRecipient) => void;
  onRemove: (userId: string) => void;
  targetType: EmailTargetType;
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Cache groups & roles (fetched once)
  const cachedGroupsRef = useRef<any[] | null>(null);
  const cachedRolesRef = useRef<any[] | null>(null);

  const fetchGroupsAndRoles = useCallback(async () => {
    const promises: Promise<void>[] = [];
    if (!cachedGroupsRef.current) {
      promises.push(
        apiClient.get(API_ENDPOINTS.GROUPS.ALL).then((res) => {
          const d = res.data?.data;
          cachedGroupsRef.current = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
        }).catch(() => { cachedGroupsRef.current = []; })
      );
    }
    if (!cachedRolesRef.current) {
      promises.push(
        apiClient.get(API_ENDPOINTS.ROLES.DROPDOWN).then((res) => {
          const d = res.data?.data;
          cachedRolesRef.current = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
        }).catch(() => { cachedRolesRef.current = []; })
      );
    }
    if (promises.length) await Promise.all(promises);
  }, []);

  const searchAll = useCallback(async (searchQuery: string) => {
    // ALL mode: no search — the single "All Users" chip is managed by the parent.
    if (targetType === 'ALL') {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const selectedIds = new Set(selectedRecipients.map((r) => r.id));
      const q = searchQuery.toLowerCase();
      let combined: any[] = [];

      if (targetType === 'USER') {
        const usersRes = await apiClient.get(API_ENDPOINTS.USERS.DROPDOWN, { params: { search: searchQuery } });
        const usersData = usersRes.data?.data;
        const users = Array.isArray(usersData) ? usersData : Array.isArray(usersData?.data) ? usersData.data : [];
        combined = users
          .filter((u: any) => !selectedIds.has(u._id))
          .map((u: any) => ({ ...u, _type: 'user' as const }));
      } else if (targetType === 'GROUP') {
        await fetchGroupsAndRoles();
        combined = (cachedGroupsRef.current || [])
          .filter((g: any) => (g.group_name || g.name || '').toLowerCase().includes(q))
          .filter((g: any) => !selectedIds.has(g.group_key || g._id))
          .map((g: any) => ({ ...g, _type: 'group' as const }));
      } else if (targetType === 'ROLE') {
        await fetchGroupsAndRoles();
        combined = (cachedRolesRef.current || [])
          .filter((r: any) => (r.role_name || r.name || '').toLowerCase().includes(q))
          .filter((r: any) => !selectedIds.has(r.role_key || r._id))
          .map((r: any) => ({ ...r, _type: 'role' as const }));
      }

      setSuggestions(combined);
      setShowDropdown(true);
      setHighlightIndex(-1);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedRecipients, fetchGroupsAndRoles, targetType]);

  const handleInputChange = (val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAll(val), 300);
  };

  const selectItem = (item: any) => {
    if (item._type === 'group') {
      const name = item.group_name || item.name;
      onAdd({ id: item.group_key || item._id, name, email: '', type: 'group' });
    } else if (item._type === 'role') {
      const name = item.role_name || item.name;
      onAdd({ id: item.role_key || item._id, name, email: '', type: 'role' });
    } else {
      const name = item.full_name || `${item.given_name || ''} ${item.family_name || ''}`.trim();
      onAdd({ id: item._id, name: name || item.email, email: item.email || '', type: 'user' });
    }
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      selectItem(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    } else if (e.key === 'Backspace' && !query && selectedRecipients.length > 0) {
      onRemove(selectedRecipients[selectedRecipients.length - 1].id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear the in-flight search whenever the target type changes so the previous
  // query / suggestions from another tab don't linger.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightIndex(-1);
    setIsLoading(false);
  }, [targetType]);

  const getSuggestionDisplay = (item: any) => {
    if (item._type === 'group') {
      return { name: item.group_name || item.name, subtitle: 'Group' };
    }
    if (item._type === 'role') {
      return { name: item.role_name || item.name, subtitle: 'Role' };
    }
    const name = item.full_name || `${item.given_name || ''} ${item.family_name || ''}`.trim();
    return { name: name || 'Unknown User', subtitle: item.email || '' };
  };

  return (
    <div className="relative flex-1">
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[40px] py-1.5 pr-3 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedRecipients.map((r) => {
          const color = getAvatarColor(r.name);
          return (
            <span
              key={r.id}
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 bg-[#F0F4FF] text-gray-800 text-xs font-medium rounded-full border border-[#D6E0F5] hover:bg-[#E4EAFC] transition-colors group"
              title={r.email || r.type}
            >
              <span
                className="w-6 h-6 rounded-full text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                {getInitials(r.name)}
              </span>
              <span className="truncate max-w-[140px]">{r.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(r.id);
                }}
                className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}

        {targetType !== 'ALL' && (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query.trim() && suggestions.length > 0) setShowDropdown(true);
            }}
            placeholder={
              selectedRecipients.length === 0
                ? targetType === 'USER'
                  ? 'Search users by name, email, or employee ID...'
                  : targetType === 'GROUP'
                  ? 'Search groups...'
                  : 'Search roles...'
                : ''
            }
            className="flex-1 min-w-[150px] bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none py-0.5"
          />
        )}

        {isLoading && (
          <div className="flex-shrink-0 w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        )}
      </div>

      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
          style={{ maxHeight: '280px', overflowY: 'auto' }}
        >
          {suggestions.map((item, index) => {
            const { name, subtitle } = getSuggestionDisplay(item);
            const color = getAvatarColor(name);
            const isHighlighted = index === highlightIndex;

            return (
              <button
                key={`${item._type}-${item._id || item.group_key || item.role_key}`}
                type="button"
                onClick={() => selectItem(item)}
                onMouseEnter={() => setHighlightIndex(index)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isHighlighted ? 'bg-[#EBF0FF]' : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className="w-9 h-9 rounded-full text-white text-sm font-bold flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {getInitials(name)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{name}</div>
                  <div className="text-xs text-gray-500 truncate">{subtitle}</div>
                </div>
              </button>
            );
          })}

          {isLoading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Searching...</div>
          )}

          {!isLoading && suggestions.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-1.5 text-xs text-brand-blue-600">
              <Search className="w-3 h-3" />
              <span>Showing top {suggestions.length} results</span>
            </div>
          )}
        </div>
      )}

      {showDropdown && !isLoading && query.trim() && suggestions.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
        >
          <div className="px-4 py-3 text-sm text-gray-500 text-center">
            No results found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Email tag input with user search (CC / BCC) ─────────────────────
function EmailSearchTagInput({
  emails,
  onChange,
  placeholder,
  autoFocus,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (email && isValidEmail(email) && !emails.includes(email)) {
      onChange([...emails, email]);
    }
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  // Search users from API
  const searchUsers = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const response: any = await apiClient.get(API_ENDPOINTS.USERS.DROPDOWN, {
        params: { search: searchQuery },
      });
      const data = response.data?.data;
      const users = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

      // Filter out users whose email is already added
      const existingEmails = new Set(emails);
      const filtered = users.filter((u: any) => u.email && !existingEmails.has(u.email.toLowerCase()));

      setSuggestions(filtered);
      setShowDropdown(true);
      setHighlightIndex(-1);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [emails]);

  // Debounced search
  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchUsers(val), 300);
  };

  // Select a user from suggestions
  const selectUser = (user: any) => {
    if (user.email) {
      addEmail(user.email);
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && showDropdown) {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      selectUser(suggestions[highlightIndex]);
    } else if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && inputValue.trim()) {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const newEmails = pasted
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => isValidEmail(s) && !emails.includes(s));
    if (newEmails.length) onChange([...emails, ...newEmails]);
  };

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email));
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1">
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[36px] py-1 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((email) => {
          const color = getAvatarColor(email);
          return (
            <span
              key={email}
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 bg-[#F0F4FF] text-gray-800 text-xs font-medium rounded-full border border-[#D6E0F5] hover:bg-[#E4EAFC] transition-colors group"
            >
              <span
                className="w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                {email[0].toUpperCase()}
              </span>
              <span className="truncate max-w-[180px]">{email}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(email);
                }}
                className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            if (inputValue.trim() && !showDropdown) addEmail(inputValue);
          }}
          onFocus={() => {
            if (inputValue.trim() && suggestions.length > 0) setShowDropdown(true);
          }}
          placeholder={emails.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[150px] bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none py-0.5"
        />
        {isLoading && (
          <div className="flex-shrink-0 w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
          style={{ maxHeight: '240px', overflowY: 'auto' }}
        >
          {suggestions.map((user, index) => {
            const name = user.full_name || `${user.given_name || ''} ${user.family_name || ''}`.trim();
            const color = getAvatarColor(name || user.email);
            const isHighlighted = index === highlightIndex;

            return (
              <button
                key={user._id}
                type="button"
                onClick={() => selectUser(user)}
                onMouseEnter={() => setHighlightIndex(index)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isHighlighted ? 'bg-[#EBF0FF]' : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {getInitials(name || user.email)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {name || 'Unknown User'}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {user.email}
                  </div>
                </div>
              </button>
            );
          })}

          {isLoading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Searching...
            </div>
          )}

          {!isLoading && suggestions.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-1.5 text-xs text-brand-blue-600">
              <Search className="w-3 h-3" />
              <span>Showing top {suggestions.length} results</span>
            </div>
          )}
        </div>
      )}

      {/* No results — show hint to type email manually */}
      {showDropdown && !isLoading && inputValue.trim() && suggestions.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
        >
          <div className="px-4 py-3 text-sm text-gray-500 text-center">
            No users found. Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">Enter</kbd> to add as email
          </div>
        </div>
      )}
    </div>
  );
}

// rgb(r, g, b) | rgba(r, g, b, a) | #hex → always a 6-char hex, or '' if unparseable
function normalizeColor(raw: string): string {
  if (!raw) return '';
  if (raw.startsWith('#')) {
    // Expand #abc → #aabbcc
    if (raw.length === 4) {
      return '#' + raw.slice(1).split('').map((c) => c + c).join('');
    }
    return raw.length >= 7 ? raw.slice(0, 7).toLowerCase() : '';
  }
  const m = raw.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return '';
  const hex = [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('');
  return `#${hex}`;
}

interface ToolbarState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  ul: boolean;
  ol: boolean;
  alignLeft: boolean;
  alignCenter: boolean;
  alignRight: boolean;
  fontSize: string;
  foreColor: string;
}

const INITIAL_TOOLBAR_STATE: ToolbarState = {
  bold: false,
  italic: false,
  underline: false,
  ul: false,
  ol: false,
  alignLeft: false,
  alignCenter: false,
  alignRight: false,
  fontSize: '',
  foreColor: '#000000',
};

// ─── Mini rich-text toolbar ──────────────────────────────────────────
function MiniToolbar({ editorRef, onInput }: { editorRef: React.RefObject<HTMLDivElement | null>; onInput: () => void }) {
  const [tbState, setTbState] = useState<ToolbarState>(INITIAL_TOOLBAR_STATE);

  // Continuously capture the last known selection INSIDE the editor so that
  // when a toolbar control (select / color picker) steals focus, we can
  // restore the exact range before running execCommand. Without this, font
  // size / color apply to the whole content (or nothing) instead of the
  // user's original selection.
  const savedRangeRef = useRef<Range | null>(null);

  const saveSelectionIfInEditor = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editorRef.current || !editorRef.current.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  }, [editorRef]);

  const restoreSavedSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  // Read the current selection's formatting and mirror it in the toolbar.
  // Only reflects state while the selection lives inside this editor.
  const refreshToolbarState = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const anchor = sel.anchorNode;
    if (!anchor || !editorRef.current || !editorRef.current.contains(anchor)) return;

    // Piggyback: whenever we refresh state, also persist the range so we have
    // an up-to-date snapshot to restore if focus shifts to a toolbar control.
    savedRangeRef.current = sel.getRangeAt(0).cloneRange();

    try {
      const fontSize = (document.queryCommandValue('fontSize') || '').toString();
      const foreColor = normalizeColor(document.queryCommandValue('foreColor') || '') || '#000000';
      setTbState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        ul: document.queryCommandState('insertUnorderedList'),
        ol: document.queryCommandState('insertOrderedList'),
        alignLeft: document.queryCommandState('justifyLeft'),
        alignCenter: document.queryCommandState('justifyCenter'),
        alignRight: document.queryCommandState('justifyRight'),
        fontSize,
        foreColor,
      });
    } catch {
      /* queryCommand may throw in some edge cases — ignore */
    }
  }, [editorRef]);

  // Sync on selection change (cursor move, arrow keys, click into editor),
  // and also after every key press so typing inside a bold run keeps the
  // Bold button lit.
  useEffect(() => {
    document.addEventListener('selectionchange', refreshToolbarState);
    return () => document.removeEventListener('selectionchange', refreshToolbarState);
  }, [refreshToolbarState]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.addEventListener('keyup', refreshToolbarState);
    el.addEventListener('mouseup', refreshToolbarState);
    el.addEventListener('focus', refreshToolbarState);
    return () => {
      el.removeEventListener('keyup', refreshToolbarState);
      el.removeEventListener('mouseup', refreshToolbarState);
      el.removeEventListener('focus', refreshToolbarState);
    };
  }, [editorRef, refreshToolbarState]);

  const exec = (cmd: string, val?: string) => {
    // Restore the last selection captured while the editor had focus. This
    // fixes font-size / color applying to the whole content when the user
    // interacts with a control that stole focus (select, color input).
    editorRef.current?.focus();
    restoreSavedSelection();
    document.execCommand(cmd, false, val);
    onInput();
    refreshToolbarState();
  };

  const Btn = ({
    onClick,
    icon: Icon,
    title,
    active,
  }: {
    onClick: () => void;
    icon: React.ElementType;
    title: string;
    active?: boolean;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault(); // Prevent editor from losing focus/selection
        onClick();
      }}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-brand-blue-100 text-brand-blue-700'
          : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
      }`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-t border-gray-200 bg-gray-50/80">
      {/* Capture phase: save the editor selection before the select takes focus */}
      <div className="w-28 flex-shrink-0" onMouseDownCapture={() => saveSelectionIfInEditor()}>
        <SimpleSelect
          compact
          value={tbState.fontSize || ''}
          onChange={(value) => {
            if (value) exec('fontSize', String(value));
          }}
          onFocus={() => saveSelectionIfInEditor()}
          options={[
            { value: '1', label: 'Small' },
            { value: '3', label: 'Normal' },
            { value: '5', label: 'Large' },
            { value: '7', label: 'Huge' },
          ]}
          placeholder="Size"
          isSearchable={false}
        />
      </div>
      <div className="w-px h-5 bg-gray-200 mx-1" />
      <Btn onClick={() => exec('bold')} icon={Bold} title="Bold (Ctrl+B)" active={tbState.bold} />
      <Btn onClick={() => exec('italic')} icon={Italic} title="Italic (Ctrl+I)" active={tbState.italic} />
      <Btn onClick={() => exec('underline')} icon={Underline} title="Underline (Ctrl+U)" active={tbState.underline} />
      <div className="w-px h-5 bg-gray-200 mx-1" />
      <Btn onClick={() => exec('justifyLeft')} icon={AlignLeft} title="Align Left" active={tbState.alignLeft} />
      <Btn onClick={() => exec('justifyCenter')} icon={AlignCenter} title="Align Center" active={tbState.alignCenter} />
      <Btn onClick={() => exec('justifyRight')} icon={AlignRight} title="Align Right" active={tbState.alignRight} />
      <div className="w-px h-5 bg-gray-200 mx-1" />
      <Btn onClick={() => exec('insertUnorderedList')} icon={List} title="Bullet List" active={tbState.ul} />
      <Btn onClick={() => exec('insertOrderedList')} icon={ListOrdered} title="Numbered List" active={tbState.ol} />
      <div className="w-px h-5 bg-gray-200 mx-1" />
      <label
        className="cursor-pointer flex items-center gap-1"
        title="Text Color"
        onMouseDown={() => saveSelectionIfInEditor()}
      >
        <input
          type="color"
          value={tbState.foreColor}
          onFocus={() => saveSelectionIfInEditor()}
          onChange={(e) => exec('foreColor', e.target.value)}
          className="w-5 h-5 border border-gray-200 rounded cursor-pointer"
        />
      </label>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────
export default function EmailComposePanel({
  value,
  onChange,
  selectedRecipients,
  onAddRecipient,
  onRemoveRecipient,
  targetType,
}: EmailComposePanelProps) {
  const [showBcc, setShowBcc] = useState(value.bcc_emails.length > 0);
  const [showCc, setShowCc] = useState(value.cc_emails.length > 0);
  const editorRef = useRef<HTMLDivElement>(null);
  // Marks that the most recent value change was caused by user typing / pasting
  // inside this editor. If so, we SKIP the parent→DOM sync (below) because
  // resetting innerHTML would wipe cursor position and inline styles that
  // `execCommand` just applied.
  const isInternalUpdateRef = useRef(false);

  // One-way sync: parent-driven content (e.g., when the modal opens) flows into
  // the DOM. User-driven changes (typing / pasting / toolbar) do NOT trigger a
  // re-sync — innerHTML is already correct.
  useEffect(() => {
    if (!editorRef.current) return;
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    if (editorRef.current.innerHTML !== value.email_html_body) {
      editorRef.current.innerHTML = value.email_html_body;
    }
  }, [value.email_html_body]);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current) {
      isInternalUpdateRef.current = true;
      onChange({ ...value, email_html_body: editorRef.current.innerHTML });
    }
  }, [onChange, value]);

  // Preserve pasted inline styles (color, font-family, font-size, bold, etc.)
  // Default browser paste into a contentEditable often gets stomped by React
  // re-renders. We explicitly sanitize + insert so styles round-trip cleanly.
  const handleEditorPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (!html && !text) return;

    e.preventDefault();

    if (html) {
      // Parse into a detached template to strip unsafe nodes / attributes
      // while keeping inline `style`, color, font-size, bold etc.
      const template = document.createElement('template');
      template.innerHTML = html;

      template.content
        .querySelectorAll('script, iframe, object, embed, form, link, meta, noscript, svg, style')
        .forEach((n) => n.remove());

      template.content.querySelectorAll<HTMLElement>('*').forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();
          // Drop event handlers + DOM identifiers + class hooks that reference
          // external stylesheets we don't have. Keep style / color / font-* /
          // align / href / src so the pasted look-and-feel survives.
          if (
            name.startsWith('on') ||
            name === 'class' ||
            name === 'id' ||
            name === 'contenteditable' ||
            name === 'draggable'
          ) {
            el.removeAttribute(attr.name);
          }
          if (name === 'href' && /^javascript:/i.test(attr.value)) {
            el.removeAttribute(attr.name);
          }
        });
      });

      // execCommand is deprecated but remains the only reliable way to insert
      // into a contentEditable while respecting current selection + undo stack.
      document.execCommand('insertHTML', false, template.innerHTML);
    } else {
      document.execCommand('insertText', false, text);
    }

    // Mark the resulting innerHTML change as internal so the sync effect skips.
    isInternalUpdateRef.current = true;
    if (editorRef.current) {
      onChange({ ...value, email_html_body: editorRef.current.innerHTML });
    }
  }, [onChange, value]);

  const update = (patch: Partial<EmailComposeData>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-visible">
      {/* ── Header bar ── */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-[#0078D4] to-[#106EBE] rounded-t-xl">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-white/90" />
          <span className="text-sm font-semibold text-white">Compose Email</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!showCc && (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="text-xs text-white/80 hover:text-white px-2 py-0.5 rounded hover:bg-white/10 transition-colors"
            >
              Cc
            </button>
          )}
          {!showBcc && (
            <button
              type="button"
              onClick={() => setShowBcc(true)}
              className="text-xs text-white/80 hover:text-white px-2 py-0.5 rounded hover:bg-white/10 transition-colors"
            >
              Bcc
            </button>
          )}
        </div>
      </div>

      {/* ── To (filtered by target_type chosen in the 4-button selector above) ── */}
      <div className="flex items-start gap-0 border-b border-gray-100 relative">
        <span className="flex-shrink-0 w-12 text-right pr-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          To
        </span>
        {targetType === 'ALL' ? (
          <div className="flex-1 py-2.5 pr-3 flex items-center">
            <span className="inline-flex items-center gap-1.5 pl-2 pr-3 py-1 bg-brand-indigo-50 text-brand-indigo-700 text-xs font-medium rounded-full border border-brand-indigo-200">
              All Active Users
            </span>
          </div>
        ) : (
          <RecipientSearchInput
            selectedRecipients={selectedRecipients}
            onAdd={onAddRecipient}
            onRemove={onRemoveRecipient}
            targetType={targetType}
          />
        )}
      </div>

      {/* ── Cc ── */}
      {showCc && (
        <div className="flex items-start gap-0 border-b border-gray-100 relative">
          <span className="flex-shrink-0 w-12 text-right pr-2 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Cc
          </span>
          <EmailSearchTagInput
            emails={value.cc_emails}
            onChange={(cc) => update({ cc_emails: cc })}
            placeholder="Type a name or email to search..."
            autoFocus
          />
          <button
            type="button"
            onClick={() => {
              setShowCc(false);
              update({ cc_emails: [] });
            }}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Bcc ── */}
      {showBcc && (
        <div className="flex items-start gap-0 border-b border-gray-100 relative">
          <span className="flex-shrink-0 w-12 text-right pr-2 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Bcc
          </span>
          <EmailSearchTagInput
            emails={value.bcc_emails}
            onChange={(bcc) => update({ bcc_emails: bcc })}
            placeholder="Type a name or email to search..."
            autoFocus
          />
          <button
            type="button"
            onClick={() => {
              setShowBcc(false);
              update({ bcc_emails: [] });
            }}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Subject ── */}
      <div className="flex items-center gap-0 border-b border-gray-200">
        <span className="flex-shrink-0 w-12 text-right pr-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Sub
        </span>
        <input
          type="text"
          value={value.email_subject}
          onChange={(e) => update({ email_subject: e.target.value })}
          placeholder="Add a subject"
          className="flex-1 py-2.5 pr-3 text-sm text-gray-800 placeholder-gray-400 bg-transparent border-none focus:outline-none focus:ring-0"
        />
      </div>

      {/* ── Toolbar at top ── */}
      <MiniToolbar editorRef={editorRef} onInput={handleEditorInput} />

      {/* ── Rich text body ── */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleEditorInput}
        onPaste={handleEditorPaste}
        data-placeholder="Compose your email here..."
        className="min-h-[200px] px-4 py-3 text-sm text-gray-800 leading-relaxed focus:outline-none"
        style={{ maxWidth: '100%', wordWrap: 'break-word', overflowWrap: 'break-word' }}
      />

      {/* ── Footer status ── */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between rounded-b-xl">
        <div className="flex items-center gap-4 text-[11px] text-gray-400">
          <span>
            {selectedRecipients.length === 0
              ? 'No recipients'
              : `${selectedRecipients.length} recipient${selectedRecipients.length !== 1 ? 's' : ''}`}
          </span>
          {value.cc_emails.length > 0 && (
            <span>{value.cc_emails.length} CC</span>
          )}
          {value.bcc_emails.length > 0 && (
            <span>{value.bcc_emails.length} BCC</span>
          )}
        </div>
      </div>

      {/* ── Editor placeholder style ── */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          font-style: italic;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] ul {
          list-style: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        [contenteditable] ol {
          list-style: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        [contenteditable] li {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  );
}
